import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Clock, CheckCircle2, AlertTriangle, Loader2, Archive, Star, RotateCcw, Send, MessageSquare } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { resolveAssetUrl } from "@/lib/url";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const STATUS = {
    new: { label: "Nowe", cls: "bg-red-100 text-red-800", Icon: AlertTriangle },
    in_progress: { label: "W trakcie realizacji", cls: "bg-amber-100 text-amber-800", Icon: Loader2 },
    resolved: { label: "Zrealizowane", cls: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 },
    archived: { label: "Zarchiwizowane", cls: "bg-gray-200 text-gray-700", Icon: Archive },
};

const PRIORITY = {
    low: { label: "Niski", cls: "bg-gray-200 text-gray-700" },
    normal: { label: "Normalny", cls: "bg-blue-100 text-blue-800" },
    high: { label: "Wysoki", cls: "bg-orange-100 text-orange-800" },
    critical: { label: "⚠ Krytyczny", cls: "bg-red-600 text-white" },
};

function fmtDate(iso) {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" }); }
    catch { return iso; }
}

export default function ReportTrackingPage() {
    const { ticket } = useParams();
    const [params] = useSearchParams();
    const token = params.get("t");
    const prefilledRating = params.get("rate");
    const [report, setReport] = useState(null);
    const [error, setError] = useState("");
    const [rating, setRating] = useState(prefilledRating ? parseInt(prefilledRating) : 0);
    const [ratingComment, setRatingComment] = useState("");
    const [ratingSubmitting, setRatingSubmitting] = useState(false);
    const [showReopen, setShowReopen] = useState(false);
    const [reopenReason, setReopenReason] = useState("");
    const [reopenSubmitting, setReopenSubmitting] = useState(false);
    const [replyText, setReplyText] = useState("");
    const [replySubmitting, setReplySubmitting] = useState(false);

    const load = useCallback(async () => {
        if (!ticket || !token) {
            setError("Brak tokenu w linku. Użyj linku z e-maila potwierdzającego.");
            return;
        }
        try {
            const { data } = await api.get(`/reports/public/${ticket}?t=${encodeURIComponent(token)}`);
            setReport(data);
            document.title = `${ticket} — Status zgłoszenia`;
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || "Nie udało się wczytać zgłoszenia.");
        }
    }, [ticket, token]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        // If a ?rate=N was in URL, scroll to rating section
        if (prefilledRating && report?.status === "resolved" && !report?.rating) {
            setTimeout(() => document.getElementById("rating-section")?.scrollIntoView({ behavior: "smooth" }), 400);
        }
    }, [prefilledRating, report]);

    const submitRating = async () => {
        if (!rating || rating < 1 || rating > 5) { toast.error("Wybierz ocenę 1-5 gwiazdek."); return; }
        setRatingSubmitting(true);
        try {
            await api.post(`/reports/public/${ticket}/rate?t=${encodeURIComponent(token)}`, { rating, comment: ratingComment });
            toast.success("Dziękujemy za ocenę!");
            await load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Nie udało się zapisać oceny.");
        } finally { setRatingSubmitting(false); }
    };

    const submitReopen = async () => {
        if (!reopenReason.trim() || reopenReason.trim().length < 5) {
            toast.error("Opisz krótko dlaczego problem nie został rozwiązany (min. 5 znaków).");
            return;
        }
        setReopenSubmitting(true);
        try {
            await api.post(`/reports/public/${ticket}/reopen?t=${encodeURIComponent(token)}`, { reason: reopenReason.trim() });
            toast.success("Zgłoszenie zostało otwarte ponownie. Wykonawca otrzymał powiadomienie.");
            setShowReopen(false);
            setReopenReason("");
            await load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Nie udało się otworzyć ponownie.");
        } finally { setReopenSubmitting(false); }
    };

    const submitReply = async () => {
        if (!replyText.trim()) return;
        setReplySubmitting(true);
        try {
            await api.post(`/reports/public/${ticket}/reply?t=${encodeURIComponent(token)}`, { text: replyText.trim() });
            setReplyText("");
            await load();
            toast.success("Twoja wiadomość została dodana.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Nie udało się wysłać.");
        } finally { setReplySubmitting(false); }
    };

    if (error) {
        return (
            <div className="min-h-screen bg-[#FDFDFD]">
                <Navbar />
                <main className="max-w-2xl mx-auto px-6 py-24 text-center">
                    <AlertTriangle className="mx-auto text-red-500 mb-4" size={48} />
                    <h1 className="font-serif text-3xl text-[#092E22] mb-3">Nie można wczytać zgłoszenia</h1>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <Link to="/zglos-usterke" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0D4F3C] text-white">
                        <ArrowLeft size={14} /> Wróć do formularza
                    </Link>
                </main>
                <Footer />
            </div>
        );
    }

    if (!report) {
        return (
            <div className="min-h-screen bg-[#FDFDFD]">
                <Navbar />
                <main className="max-w-2xl mx-auto px-6 py-24 text-center text-gray-500">Wczytywanie...</main>
                <Footer />
            </div>
        );
    }

    const statusBadge = STATUS[report.status] || STATUS.new;
    const prioBadge = PRIORITY[report.priority] || PRIORITY.normal;
    const StatusIcon = statusBadge.Icon;

    const reporterNotes = (report.worker_notes || []).filter((n) => n.author_role === "reporter");
    const workerAdminNotes = (report.worker_notes || []).filter((n) => n.author_role !== "reporter");

    return (
        <div data-testid="report-tracking-page" className="min-h-screen bg-[#FDFDFD]">
            <Navbar />

            {/* Hero */}
            <header className="bg-[#092E22] text-white pt-32 pb-14 relative overflow-hidden">
                <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#D4A537]/15 blur-3xl" />
                <div className="max-w-4xl mx-auto px-6 lg:px-10 relative z-10">
                    <Link to="/" className="inline-flex items-center gap-2 text-[#D4A537] text-sm hover:underline">
                        <ArrowLeft size={14} /> Strona główna
                    </Link>
                    <div className="mt-6 flex items-center gap-3 flex-wrap">
                        <span className="inline-flex items-center px-3 py-1 rounded-md bg-[#D4A537] text-[#092E22] font-mono font-bold tracking-wider text-sm" data-testid="tracking-ticket">
                            {report.ticket_number}
                        </span>
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs uppercase tracking-wider font-semibold ${statusBadge.cls}`} data-testid="tracking-status">
                            <StatusIcon size={12} /> {statusBadge.label}
                        </span>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${prioBadge.cls}`}>
                            Priorytet: {prioBadge.label}
                        </span>
                    </div>
                    <h1 className="font-serif text-3xl md:text-4xl mt-4 leading-tight">
                        Status zgłoszenia: {report.category_label}
                    </h1>
                    <p className="text-white/70 text-sm mt-2">
                        Zgłoszone {fmtDate(report.created_at)}
                        {report.location && ` • Lokalizacja: ${report.location}`}
                    </p>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-6 lg:px-10 py-12 space-y-8">
                {/* Status progress */}
                <section className="bg-white rounded-3xl shadow-sm border border-gray-100 p-7">
                    <h2 className="font-serif text-xl text-[#092E22] mb-5">Postęp realizacji</h2>
                    <div className="grid grid-cols-3 gap-2">
                        {[
                            { key: "new", label: "Zgłoszone", at: report.created_at },
                            { key: "in_progress", label: "W trakcie", at: report.started_at },
                            { key: "resolved", label: "Zrealizowane", at: report.resolved_at },
                        ].map((step, i) => {
                            const active = !!step.at;
                            const current = report.status === step.key;
                            return (
                                <div key={step.key} className="relative" data-testid={`tracking-step-${step.key}`}>
                                    <div className={`flex flex-col items-center gap-1.5 ${active ? "text-[#0D4F3C]" : "text-gray-300"}`}>
                                        <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm ${active ? (current ? "bg-[#D4A537] text-[#092E22]" : "bg-[#0D4F3C] text-white") : "bg-gray-100"}`}>
                                            {active ? "✓" : i + 1}
                                        </div>
                                        <p className="text-xs font-semibold text-center">{step.label}</p>
                                        {step.at && <p className="text-[10px] text-gray-400">{fmtDate(step.at)}</p>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* Original report */}
                <section className="bg-white rounded-3xl shadow-sm border border-gray-100 p-7">
                    <h2 className="font-serif text-xl text-[#092E22] mb-3">Twoje zgłoszenie</h2>
                    <p className="text-sm text-gray-700 whitespace-pre-line bg-[#FAF8F3] rounded-xl p-4 mb-4">{report.description}</p>
                    {(report.images || []).length > 0 && (
                        <div className="flex flex-wrap gap-2" data-testid="tracking-reporter-images">
                            {report.images.map((u, i) => (
                                <a key={i} href={resolveAssetUrl(u)} target="_blank" rel="noopener noreferrer" className="w-28 h-28 rounded-xl overflow-hidden bg-gray-100 border border-gray-200">
                                    <img src={resolveAssetUrl(u)} alt={`Zdjęcie ${i + 1}`} className="w-full h-full object-cover" />
                                </a>
                            ))}
                        </div>
                    )}
                </section>

                {/* Worker/admin notes */}
                {workerAdminNotes.length > 0 && (
                    <section className="bg-white rounded-3xl shadow-sm border border-gray-100 p-7">
                        <h2 className="font-serif text-xl text-[#092E22] mb-4">Komentarze od wykonawcy</h2>
                        <ul className="space-y-3" data-testid="tracking-worker-notes">
                            {workerAdminNotes.map((n) => (
                                <li key={n.id} className="bg-blue-50 rounded-xl p-4">
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                        <span className="text-xs font-semibold text-[#092E22]">{n.author_name}</span>
                                        <span className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full bg-blue-200 text-blue-900">{n.author_role === "admin" ? "admin" : "wykonawca"}</span>
                                        <span className="text-[10px] text-gray-400">{fmtDate(n.created_at)}</span>
                                    </div>
                                    <p className="text-sm text-gray-800 whitespace-pre-line">{n.text}</p>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {/* Proof images */}
                {(report.proof_images || []).length > 0 && (
                    <section className="bg-white rounded-3xl shadow-sm border border-gray-100 p-7">
                        <h2 className="font-serif text-xl text-[#092E22] mb-4">Zdjęcia realizacji</h2>
                        <div className="flex flex-wrap gap-2" data-testid="tracking-proof-images">
                            {report.proof_images.map((u, i) => (
                                <a key={i} href={resolveAssetUrl(u)} target="_blank" rel="noopener noreferrer" className="w-32 h-32 rounded-xl overflow-hidden bg-gray-100 border border-emerald-200">
                                    <img src={resolveAssetUrl(u)} alt={`Realizacja ${i + 1}`} className="w-full h-full object-cover" />
                                </a>
                            ))}
                        </div>
                    </section>
                )}

                {/* Reply box (always available) */}
                <section className="bg-white rounded-3xl shadow-sm border border-gray-100 p-7">
                    <h2 className="font-serif text-xl text-[#092E22] mb-2 flex items-center gap-2">
                        <MessageSquare size={18} /> Dodaj informację
                    </h2>
                    <p className="text-sm text-gray-500 mb-3">Masz coś do dodania? Pytanie dla wykonawcy? Wiadomość zostanie dodana do historii zgłoszenia.</p>
                    <Textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        rows={3}
                        placeholder="Napisz wiadomość..."
                        className="rounded-2xl mb-3"
                        data-testid="tracking-reply-input"
                    />
                    <Button onClick={submitReply} disabled={replySubmitting || !replyText.trim()} data-testid="tracking-reply-submit" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full">
                        <Send size={14} className="mr-2" /> {replySubmitting ? "Wysyłanie..." : "Wyślij"}
                    </Button>
                    {reporterNotes.length > 0 && (
                        <div className="mt-5 pt-5 border-t border-gray-100 space-y-2" data-testid="tracking-reporter-notes">
                            <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Twoje wiadomości</p>
                            {reporterNotes.map((n) => (
                                <div key={n.id} className="bg-[#FAF8F3] rounded-xl p-3 text-sm">
                                    <p className="text-[10px] text-gray-400 mb-1">{fmtDate(n.created_at)}</p>
                                    <p className="text-gray-800 whitespace-pre-line">{n.text}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {/* Rating & reopen (only after resolved) */}
                {report.status === "resolved" && (
                    <section id="rating-section" className="bg-gradient-to-br from-[#FFFBEB] to-white rounded-3xl shadow-sm border border-amber-200 p-7">
                        <h2 className="font-serif text-xl text-[#092E22] mb-3">Jak oceniasz realizację?</h2>
                        {report.rating ? (
                            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-center" data-testid="tracking-rating-done">
                                <div className="flex justify-center gap-1 mb-2">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <Star key={n} size={22} className={n <= report.rating ? "fill-[#D4A537] text-[#D4A537]" : "text-gray-200"} />
                                    ))}
                                </div>
                                <p className="text-sm text-emerald-900 font-semibold">Dziękujemy za ocenę ({report.rating}/5)!</p>
                                {report.rating_comment && <p className="text-xs text-gray-500 mt-2 italic">„{report.rating_comment}"</p>}
                            </div>
                        ) : (
                            <>
                                <div className="flex justify-center gap-1 my-5" data-testid="tracking-rating-stars">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <button
                                            key={n}
                                            onClick={() => setRating(n)}
                                            data-testid={`tracking-rating-${n}`}
                                            className="p-1 transition-transform hover:scale-110"
                                        >
                                            <Star size={38} className={n <= rating ? "fill-[#D4A537] text-[#D4A537]" : "text-gray-200 hover:text-[#D4A537]/50"} />
                                        </button>
                                    ))}
                                </div>
                                <Textarea
                                    value={ratingComment}
                                    onChange={(e) => setRatingComment(e.target.value)}
                                    rows={3}
                                    placeholder="Komentarz (opcjonalnie): co było dobrze, co można poprawić?"
                                    className="rounded-2xl mb-3"
                                    data-testid="tracking-rating-comment"
                                />
                                <div className="flex gap-2 justify-end">
                                    <Button onClick={submitRating} disabled={!rating || ratingSubmitting} data-testid="tracking-rating-submit" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full">
                                        {ratingSubmitting ? "Wysyłanie..." : "Wyślij ocenę"}
                                    </Button>
                                </div>
                            </>
                        )}

                        {/* Reopen */}
                        <div className="mt-6 pt-6 border-t border-amber-200">
                            {!showReopen ? (
                                <button onClick={() => setShowReopen(true)} data-testid="tracking-reopen-btn" className="inline-flex items-center gap-2 text-sm text-red-700 hover:text-red-900 font-medium underline underline-offset-2">
                                    <RotateCcw size={14} /> Problem nie został rozwiązany — otwórz ponownie
                                </button>
                            ) : (
                                <div className="bg-red-50 border border-red-200 rounded-2xl p-5 space-y-3" data-testid="tracking-reopen-form">
                                    <h3 className="font-semibold text-red-900">Otwórz ponownie zgłoszenie</h3>
                                    <p className="text-xs text-red-800">Opisz co nie zostało rozwiązane — wykonawca otrzyma natychmiastowe powiadomienie.</p>
                                    <Textarea
                                        value={reopenReason}
                                        onChange={(e) => setReopenReason(e.target.value)}
                                        rows={3}
                                        placeholder="np. Dioda w windzie dalej nie działa, pod ścianą nadal słychać hałas..."
                                        data-testid="tracking-reopen-reason"
                                        className="rounded-xl"
                                    />
                                    <div className="flex gap-2 justify-end">
                                        <Button variant="outline" onClick={() => { setShowReopen(false); setReopenReason(""); }} className="rounded-full">Anuluj</Button>
                                        <Button onClick={submitReopen} disabled={reopenSubmitting} data-testid="tracking-reopen-submit" className="bg-red-600 hover:bg-red-700 text-white rounded-full">
                                            <RotateCcw size={14} className="mr-2" /> {reopenSubmitting ? "..." : "Otwórz ponownie"}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>
                )}

                {/* History */}
                {(report.status_history || []).length > 1 && (
                    <section className="bg-white rounded-3xl shadow-sm border border-gray-100 p-7">
                        <h2 className="font-serif text-xl text-[#092E22] mb-4">Historia zmian</h2>
                        <ol className="space-y-2 text-sm" data-testid="tracking-history">
                            {report.status_history.map((h, i) => {
                                const b = STATUS[h.status] || { label: h.status, cls: "bg-gray-200" };
                                return (
                                    <li key={i} className="flex items-center gap-3 text-xs">
                                        <Clock size={12} className="text-gray-400" />
                                        <span className={`px-2 py-0.5 rounded-full font-semibold ${b.cls}`}>{b.label}</span>
                                        <span className="text-gray-500">{fmtDate(h.at)}</span>
                                    </li>
                                );
                            })}
                        </ol>
                    </section>
                )}
            </main>

            <Footer />
        </div>
    );
}
